

import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { View, UserSettings, Notification } from '../../types';
import { UI_CONFIG, FEATURE_FLAGS } from '../../config';
import { getHomeworkFeatureLabel } from '../../utils/homeworkLabels';
import { studentLogoutAll, formatAssetUrl } from '../../services/apiService';
import { BookOpenIcon, SparklesIcon, ChevronDownIcon, Bars3Icon, XMarkIcon } from './Icons';
import NotificationBell from './NotificationBell';

interface HeaderProps {
  onNavigate: (view: View | string, payload?: object) => void;
  currentView: View;
  user: UserSettings | null;
  onLogout: () => void;
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClearRead: () => void;
  protectedViews: Set<View>;
  /** Hiển thị tên ứng dụng cạnh logo (từ config backend) */
  showAppNameInHeader?: boolean;
  /** Tên ứng dụng (từ config backend) */
  appName?: string;
  onSwitchRole?: (role: 'teacher' | 'parent') => void;
  activeRoleConfig: {
    role: string;
    accountRole: string;
    isTeacher: boolean;
    isParent: boolean;
    isStudent: boolean;
    isGuest: boolean;
    canSwitchRole: boolean;
    isRoleSwitched: boolean;
    dashboardView: View;
    dashboardPath: string;
    dashboardLabel: string;
    features: {
      showTeacherTools: boolean;
      showParentTools: boolean;
      showStudentTools: boolean;
      showCreative: boolean;
      showAssistant: boolean;
      showAITools: boolean;
      showEntertainment: boolean;
      homeworkLabel: string;
    };
  };
}

const Header: React.FC<HeaderProps> = ({ 
    onNavigate, 
    currentView, 
    user, 
    onLogout, 
    notifications, 
    onMarkAsRead, 
    onMarkAllAsRead, 
    onClearRead, 
    protectedViews, 
    showAppNameInHeader, 
    appName, 
    onSwitchRole,
    activeRoleConfig
}) => {
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const mobileNavRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const isFeatureLandingActive = location.pathname === '/tinh-nang' || location.pathname.startsWith('/tinh-nang/');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
            if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
                setIsMobileNavOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleNav = (view: View, payload: object = {}) => {
        if (!user && protectedViews.has(view)) {
            // User is not logged in and trying to access a protected page
            onNavigate(View.Login, { redirect: { view, payload } });
        } else {
            // User is logged in OR accessing a public page
            onNavigate(view, payload);
        }
        setIsUserMenuOpen(false);
        setIsMobileNavOpen(false);
    };

    /** Dieu huong tu do theo path string - phuc vu cac route public ngoai View enum (vd: /tinh-nang). */
    const handleNavToPath = (path: string) => {
        onNavigate(path);
        setIsUserMenuOpen(false);
        setIsMobileNavOpen(false);
    };

    const DropdownLink: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
        return (
            <div className="relative group">
                <button 
                    className="px-3 py-2 rounded-md text-sm font-semibold text-gray-600 hover:bg-gray-200 flex items-center space-x-1 focus:outline-none"
                    aria-haspopup="true"
                >
                    <span>{title}</span>
                    <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:rotate-180" />
                </button>
                
                <div 
                    className="absolute left-0 top-full pt-2 w-56 z-[60] transition-opacity duration-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible"
                >
                    <div className="w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                        <div className="py-1">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const DropdownItem: React.FC<{ view: View; payload?: object; children: React.ReactNode }> = ({ view, payload, children }) => (
        <button onClick={() => handleNav(view, payload)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
            {children}
        </button>
    );

    const TopNavLink: React.FC<{ path: string; active?: boolean; children: React.ReactNode; icon?: React.ReactNode }> = ({ path, active, children, icon }) => (
        <button
            onClick={() => handleNavToPath(path)}
            className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-colors focus:outline-none ${
                active ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-200'
            }`}
        >
            {icon}
            <span>{children}</span>
        </button>
    );
    
    const renderNavLinks = () => {
        const hocTapItems = [
            { label: 'Thư viện cộng đồng', view: View.SharedLibrary },
            { label: 'Luyện phát âm', view: View.Pronunciation },
        ];

        const aiToolsItems = [
            { label: 'Phòng Lab AI (NLc)', view: View.AILab },
            { label: 'Thử thách Đạo đức AI (NLb)', view: View.AIEthics },
            { label: 'Kiểm chứng AI (NLa)', view: View.AIFactCheck },
            { label: 'Hành trình khám phá nghề nghiệp', view: View.CareerJourney },
        ];

        const teacherCreativeItems = [
            { label: 'Hoạt động Sáng tạo', view: View.ActivityCreator },
            { label: 'Video tương tác', view: View.InteractiveVideo },
            { label: 'Thiết kế ứng dụng 3D', view: View.ThietKe3DCreator },
            { label: 'Trợ lý viết SKKN', view: View.SKKNCreator },
            ...(FEATURE_FLAGS.VIDEO_GENERATION !== false ? [{ label: 'Tạo Video bài giảng', view: View.VideoCreator }] as const : []),
            ...(FEATURE_FLAGS.IMAGE_GENERATION !== false ? [{ label: 'Thiết kế thông minh', view: View.DesignCenter }] as const : []),
        ];

        const parentCreativeItems = [
            { label: 'Tạo trình chiếu', view: View.SlideCreator },
            { label: activeRoleConfig.features.homeworkLabel, view: View.HomeworkCreator },
            { label: 'Thiết kế ứng dụng 3D', view: View.ThietKe3DCreator },
            ...(FEATURE_FLAGS.IMAGE_GENERATION !== false ? [{ label: 'Thiết kế thông minh', view: View.DesignCenter }] as const : []),
        ];
        
        const giaiTriItems = [
            { label: 'Trò chơi Giáo dục', view: View.GameCreator },
        ];

        const troGiangItems = [
            { label: 'Soạn giáo án', view: View.LessonPlanCreator },
            { label: 'Tạo trình chiếu', view: View.SlideCreator },
            { label: 'Tạo bài kiểm tra', view: View.QuizCreator },
            { label: activeRoleConfig.features.homeworkLabel, view: View.HomeworkCreator },
            { label: 'Kiểm tra kiến thức', view: View.OralQuizCreator },
            { label: 'Tạo trò chơi', view: View.GameCreator },
            { label: 'Mô phỏng khoa học', view: View.ScientificSimulationCreator },
            { label: 'Quản lý lớp học', view: View.ClassManagement },
        ];

        const troLyItems = [
            { label: 'Trợ lý Lịch sử', view: View.HistoryStoryteller },
            { label: 'Trợ lý Văn học', view: View.LiteratureAssistant },
            { label: 'Trợ lý Khoa học', view: View.ScienceExplainer },
        ];
        
        return (
             <>
                <TopNavLink
                    path="/tinh-nang"
                    active={isFeatureLandingActive}
                    icon={<SparklesIcon className="h-4 w-4 text-teal-500" />}
                >
                    Tính năng
                </TopNavLink>
                <DropdownLink title="Học tập">
                    {hocTapItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                </DropdownLink>
                {activeRoleConfig.isTeacher && (
                    <>
                        <DropdownLink title="Sáng tạo">
                            {teacherCreativeItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                        </DropdownLink>
                        <DropdownLink title="Giải trí">
                            {giaiTriItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                        </DropdownLink>
                        <DropdownLink title="Trợ giảng">
                            {troGiangItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                        </DropdownLink>
                    </>
                )}
                {activeRoleConfig.isParent && (
                    <>
                        <DropdownLink title="Sáng tạo">
                            {parentCreativeItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                        </DropdownLink>
                        <DropdownLink title="Giải trí">
                            {giaiTriItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                        </DropdownLink>
                    </>
                )}
                <DropdownLink title="Trợ lý">
                    {troLyItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                </DropdownLink>
                <DropdownLink title="Công cụ AI">
                    {aiToolsItems.map(item => <DropdownItem key={item.view} view={item.view}>{item.label}</DropdownItem>)}
                </DropdownLink>
            </>
        );
    };

    const renderMobileNavContent = () => {
        const hocTapItems = [
            { label: 'Thư viện cộng đồng', view: View.SharedLibrary },
            { label: 'Luyện phát âm', view: View.Pronunciation },
        ];
        const teacherCreativeItems = [
            { label: 'Hoạt động Sáng tạo', view: View.ActivityCreator },
            { label: 'Video tương tác', view: View.InteractiveVideo },
            { label: 'Thiết kế ứng dụng 3D', view: View.ThietKe3DCreator },
            { label: 'Trợ lý viết SKKN', view: View.SKKNCreator },
            ...(FEATURE_FLAGS.VIDEO_GENERATION !== false ? [{ label: 'Tạo Video bài giảng', view: View.VideoCreator }] as const : []),
            ...(FEATURE_FLAGS.IMAGE_GENERATION !== false ? [{ label: 'Thiết kế thông minh', view: View.DesignCenter }] as const : []),
        ];
        const parentCreativeItems = [
            { label: 'Tạo trình chiếu', view: View.SlideCreator },
            { label: activeRoleConfig.features.homeworkLabel, view: View.HomeworkCreator },
            { label: 'Thiết kế ứng dụng 3D', view: View.ThietKe3DCreator },
            ...(FEATURE_FLAGS.IMAGE_GENERATION !== false ? [{ label: 'Thiết kế thông minh', view: View.DesignCenter }] as const : []),
        ];
        const giaiTriItems = [{ label: 'Trò chơi Giáo dục', view: View.GameCreator }];
        const troGiangItems = [
            { label: 'Soạn giáo án', view: View.LessonPlanCreator },
            { label: 'Tạo trình chiếu', view: View.SlideCreator },
            { label: 'Tạo bài kiểm tra', view: View.QuizCreator },
            { label: activeRoleConfig.features.homeworkLabel, view: View.HomeworkCreator },
            { label: 'Kiểm tra kiến thức', view: View.OralQuizCreator },
            { label: 'Tạo trò chơi', view: View.GameCreator },
            { label: 'Mô phỏng khoa học', view: View.ScientificSimulationCreator },
            { label: 'Quản lý lớp học', view: View.ClassManagement },
        ];
        const troLyItems = [
            { label: 'Trợ lý Lịch sử', view: View.HistoryStoryteller },
            { label: 'Trợ lý Văn học', view: View.LiteratureAssistant },
            { label: 'Trợ lý Khoa học', view: View.ScienceExplainer },
        ];
        const aiToolsItems = [
            { label: 'Phòng Lab AI', view: View.AILab },
            { label: 'Đạo đức AI', view: View.AIEthics },
            { label: 'Kiểm chứng AI', view: View.AIFactCheck },
            { label: 'Hành trình khám phá', view: View.CareerJourney },
        ];

        const NavSection: React.FC<{ title: string; items: { label: string; view: View }[] }> = ({ title, items }) => (
            <div className="border-b border-gray-100 last:border-0">
                <p className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</p>
                {items.map(item => (
                    <button key={item.view} onClick={() => handleNav(item.view)} className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100">
                        {item.label}
                    </button>
                ))}
            </div>
        );
        return (
            <div className="py-4">
                <div className="px-4 py-3 border-b border-gray-100 space-y-1">
                    <button onClick={handleDashboardNav} className="flex items-center gap-2 w-full text-left px-2 py-2 text-sm font-bold text-teal-600 hover:bg-teal-50 rounded-lg">
                        <BookOpenIcon className="h-5 w-5" />
                        {activeRoleConfig.dashboardLabel}
                    </button>
                    <button
                        onClick={() => handleNavToPath('/tinh-nang')}
                        className={`flex items-center gap-2 w-full text-left px-2 py-2 text-sm font-bold rounded-lg ${
                            isFeatureLandingActive ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <SparklesIcon className="h-5 w-5 text-teal-500" />
                        Khám phá tính năng
                    </button>
                </div>
                {user && user.authType !== 'student_code' && (
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
                        <button onClick={() => handleNav(View.CreditStore)} className="flex items-center gap-2 flex-1 px-4 py-2.5 bg-teal-50 text-teal-700 rounded-xl text-sm font-bold">
                            <SparklesIcon className="h-5 w-5" />
                            Mua {UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}
                        </button>
                        <span className="text-lg font-black text-amber-700">{(user.totalCredits || 0).toLocaleString('vi-VN')}</span>
                    </div>
                )}
                <NavSection title="Học tập" items={hocTapItems} />
                {activeRoleConfig.isTeacher && <NavSection title="Sáng tạo" items={teacherCreativeItems} />}
                {activeRoleConfig.isParent && <NavSection title="Sáng tạo" items={parentCreativeItems} />}
                {(activeRoleConfig.isTeacher || activeRoleConfig.isParent) && <NavSection title="Giải trí" items={giaiTriItems} />}
                {activeRoleConfig.isTeacher && <NavSection title="Trợ giảng" items={troGiangItems} />}
                <NavSection title="Trợ lý" items={troLyItems} />
                <NavSection title="Công cụ AI" items={aiToolsItems} />
                {user && (
                    <div className="border-t border-gray-100 px-4 py-2">
                        <p className="px-2 py-1 text-xs font-bold text-gray-500 uppercase tracking-wider">Tài khoản</p>
                        {user.authType !== 'student_code' && (
                            <button type="button" onClick={() => handleNav(View.AffiliateDashboard)} className="block w-full text-left px-2 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 rounded-lg">
                                {user.isAffiliateActive ? 'Trung tâm Affiliate' : 'Bật kiếm tiền'}
                            </button>
                        )}
                        {activeRoleConfig.isTeacher && (
                            <button type="button" onClick={() => handleNav(View.Settings, { hash: 'quan-ly-nhom' })} className="block w-full text-left px-2 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 rounded-lg">
                                Quản lý nhóm
                            </button>
                        )}
                        <button type="button" onClick={() => handleNav(View.Settings)} className="block w-full text-left px-2 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                            Cài đặt
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const handleDashboardNav = () => {
        handleNav(activeRoleConfig.dashboardView);
    };

    return (
        <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-14">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => handleNav(View.Dashboard)}
                            className="flex items-center gap-2 shrink-0 py-1 hover:opacity-90 transition-opacity"
                            title="Trang chủ"
                        >
                            {UI_CONFIG.APP_LOGO_URL ? (
                                <img
                                    src={formatAssetUrl(UI_CONFIG.APP_LOGO_URL) || UI_CONFIG.APP_LOGO_URL}
                                    alt="Logo"
                                    className="h-11 sm:h-12 w-auto max-w-[220px] object-contain shrink-0"
                                    decoding="async"
                                />
                            ) : (
                                <BookOpenIcon className="h-10 w-10 text-teal-600 shrink-0" />
                            )}
                            {(showAppNameInHeader ?? UI_CONFIG.SHOW_APP_NAME_IN_HEADER !== false) && (
                                <span className="text-base font-bold text-gray-800 leading-tight">{appName ?? UI_CONFIG.APP_NAME}</span>
                            )}
                        </button>
                        <nav className="hidden md:flex space-x-1">
                           {renderNavLinks()}
                        </nav>
                        <div className="md:hidden" ref={mobileNavRef}>
                            <button
                                onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                                aria-label={isMobileNavOpen ? 'Đóng menu' : 'Mở menu'}
                            >
                                {isMobileNavOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
                            </button>
                            {isMobileNavOpen && (
                                <div className="absolute left-0 right-0 top-14 bg-white shadow-xl border-b border-gray-200 max-h-[calc(100vh-3.5rem)] overflow-y-auto z-40 animate-in slide-in-from-top-2 duration-200">
                                    {renderMobileNavContent()}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        {user ? (
                            <>
                                {user.authType !== 'student_code' && (
                                    <>
                                        <button onClick={() => handleNav(View.CreditStore)} className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm font-bold transition-colors">
                                            <SparklesIcon className="h-5 w-5" />
                                            <span>Mua {UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}</span>
                                        </button>
                                        <div className="hidden sm:flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-semibold">
                                            <span>{(user.totalCredits || 0).toLocaleString('vi-VN')} {UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}</span>
                                            <button onClick={() => handleNav(View.CreditStore)} className="ml-1 text-yellow-900 font-bold hover:underline" title={`Nạp thêm ${UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}`}>+</button>
                                        </div>
                                    </>
                                )}
                                <NotificationBell 
                                    notifications={notifications} 
                                    onNavigate={onNavigate} 
                                    onMarkAsRead={onMarkAsRead}
                                    onMarkAllAsRead={onMarkAllAsRead}
                                    onClearRead={onClearRead}
                                />
                                <div className="relative" ref={userMenuRef}>
                                    <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-2 min-w-0" title={user.teacherName}>
                                        <div className="shrink-0 h-9 w-9 rounded-full overflow-hidden bg-teal-500 flex items-center justify-center text-white font-bold text-sm">
                                            {user.avatar ? (
                                                <img src={formatAssetUrl(user.avatar)} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                user.teacherName.charAt(0).toUpperCase()
                                            )}
                                        </div>
                                        <span className="hidden lg:block text-sm font-semibold text-gray-700 text-left leading-tight max-w-[5.5rem] line-clamp-2 break-words">
                                            {user.teacherName}
                                        </span>
                                    </button>
                                    {isUserMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-20">
                                            <button onClick={handleDashboardNav} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{activeRoleConfig.dashboardLabel}</button>
                                            <button onClick={() => handleNav(View.DocumentLibrary)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Thư viện của tôi</button>
                                            
                                            {/* Switch Role Feature for Teacher/Parent (Chế độ xem thông minh) */}
                                            {onSwitchRole && activeRoleConfig.canSwitchRole && (
                                                <button 
                                                    onClick={() => {
                                                        const targetRole = activeRoleConfig.isRoleSwitched ? 'teacher' : 'parent';
                                                        onSwitchRole(targetRole);
                                                        setIsUserMenuOpen(false);
                                                    }} 
                                                    className="block w-full text-left px-4 py-2 text-sm font-medium text-teal-600 hover:bg-teal-50 border-y border-gray-50 my-1"
                                                >
                                                    {activeRoleConfig.isRoleSwitched ? 'Về vai trò Giáo viên' : 'Sang vai trò Phụ huynh'}
                                                </button>
                                            )}

                                            <div className="border-t my-1"></div>
                                            <button onClick={() => handleNav(View.Settings)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Cài đặt</button>
                                            {activeRoleConfig.isTeacher && (
                                                <button onClick={() => handleNav(View.Settings, { hash: 'quan-ly-nhom' })} className="block w-full text-left px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50">Quản lý nhóm</button>
                                            )}
                                            {user.authType !== 'student_code' && (
                                                <>
                                                    <button onClick={() => handleNav(View.CreditStore)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Mua {UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}</button>
                                                    <button onClick={() => handleNav(View.TransactionHistory)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Lịch sử giao dịch</button>
                                                    <button onClick={() => handleNav(View.AffiliateDashboard)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{user.isAffiliateActive ? 'Trung tâm Affiliate' : 'Bật kiếm tiền'}</button>
                                                </>
                                            )}
                                            {user.authType === 'student_code' && (
                                                <button onClick={async () => { try { await studentLogoutAll(); } catch {} onLogout(); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-gray-100">Đăng xuất tất cả thiết bị</button>
                                            )}
                                            <div className="border-t my-1"></div>
                                            <button onClick={() => { onLogout(); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">Đăng xuất</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <button onClick={() => handleNav(View.Login)} className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-md hover:bg-teal-700">
                                Đăng nhập
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
